import type { MfgPlant, MfgUnit } from "@/lib/manufacturingClient";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

const SALE_PATH_LABELS: Record<string, string> = {
  inventory_no_sale_yet: "Inventory (no sale yet)",
  cash: "Cash sale",
  finance: "Finance",
  rto: "Rent-to-own",
};

const VISIBILITY_LABELS: Record<string, string> = {
  private: "Private",
  org_internal: "Org internal",
  cross_dealer_marketplace: "Cross-dealer marketplace",
};

export { SALE_PATH_LABELS, VISIBILITY_LABELS };

function plantName(plants: MfgPlant[], plantId: string): string {
  const p = plants.find((x) => x.id === plantId);
  return p ? `${p.plant_code} · ${p.name}` : plantId.slice(0, 8);
}

function formatTimestamp(ts: string | null): string | null {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export function ManufacturingUnitRow({
  unit,
  plants,
  actionBusy,
  onStartBuild,
  onCompleteBuild,
}: {
  unit: MfgUnit;
  plants: MfgPlant[];
  actionBusy: boolean;
  onStartBuild: (unitId: string) => void;
  onCompleteBuild: (unitId: string) => void;
}) {
  const created = formatTimestamp(unit.created_at);
  const buildStarted = formatTimestamp(unit.build_started_at);
  const buildCompleted = formatTimestamp(unit.build_completed_at);
  const canStartBuild = !unit.serial && unit.origin === "manufactured";
  const canCompleteBuild = Boolean(unit.serial) && !unit.build_completed_at;
  return (
    <li className="flex flex-wrap items-start gap-2 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
          <code>{unit.display_label}</code>
          {unit.serial ? null : (
            <span className="rounded-md bg-gray-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Pre-build
            </span>
          )}
          <span className="rounded-md bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {SALE_PATH_LABELS[unit.sale_path] ?? unit.sale_path}
          </span>
          {unit.visibility !== "private" ? (
            <span className="rounded-md bg-fuchsia-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              {VISIBILITY_LABELS[unit.visibility] ?? unit.visibility}
            </span>
          ) : null}
        </div>
        <div className="text-xs text-gray-600 dark:text-zinc-400">
          Plant: {plantName(plants, unit.plant_id)}
        </div>
        {unit.inventory_holder_org_id ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Held by dealer org: <code>{unit.inventory_holder_org_id}</code>
          </div>
        ) : null}
        {unit.end_customer_contact_id ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            End customer: <code>{unit.end_customer_contact_id}</code>
          </div>
        ) : null}
        {unit.ops_deal_id ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Deal: <code>{unit.ops_deal_id}</code>
          </div>
        ) : null}
        {unit.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{unit.notes}</div>
        ) : null}
        {created ? (
          <div className="text-xs text-gray-500 dark:text-zinc-500">Created {created}</div>
        ) : null}
        {buildStarted ? (
          <div className="text-xs text-gray-500 dark:text-zinc-500">Build started {buildStarted}</div>
        ) : null}
        {buildCompleted ? (
          <div className="text-xs text-gray-500 dark:text-zinc-500">Build complete {buildCompleted}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col gap-1">
        {canStartBuild ? (
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={actionBusy}
            onClick={() => onStartBuild(unit.id)}
          >
            Issue serial / start build
          </button>
        ) : null}
        {canCompleteBuild ? (
          <button
            type="button"
            className={ghostBtnClass}
            disabled={actionBusy}
            onClick={() => onCompleteBuild(unit.id)}
          >
            Mark build complete
          </button>
        ) : null}
      </div>
    </li>
  );
}
